import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { canSee } from "../lib/roles";
import { api } from "../lib/api";
import {
  LayoutDashboard, Users, Search, Sparkles, CheckSquare, TrendingUp, Settings, Bell, MessageCircle,
  Globe, Contact, ListOrdered, BriefcaseBusiness, Megaphone, ClipboardList, ChevronDown, AlertTriangle,
  CalendarDays,
  Cloud,
  Euro,
} from "lucide-react";

export const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/hermes", label: "Hermes AI", icon: Sparkles },
  { label: "Vendite", icon: BriefcaseBusiness, children: [
    { to: "/venditori", label: "Venditori", icon: Users },
    { to: "/lead-explorer", label: "Lead Explorer", icon: Search },
    { to: "/costi", label: "Costi", icon: Euro, onlyIfNoMarketing: true },
  ] },
  { label: "Marketing", icon: Megaphone, children: [
    { to: "/meta-ads", label: "Meta Ads", icon: TrendingUp },
    { to: "/clienti", label: "Clienti", icon: Users },
    { to: "/google-ads", label: "Google Ads", icon: Globe },
    { to: "/clickup", label: "ClickUp", icon: CheckSquare },
  ] },
  { label: "Segreteria", icon: ClipboardList, children: [
    { to: "/attivita", label: "Reminder", icon: Bell },
    { to: "/avvisi", label: "Avvisi", icon: AlertTriangle },
    { to: "/whatsapp", label: "Report", icon: MessageCircle, end: true },
    { to: "/whatsapp/registro", label: "Registro messaggi", icon: ListOrdered },
    { to: "/persone", label: "Rubrica", icon: Contact },
    { to: "/calendario", label: "Calendario", icon: CalendarDays },
    { to: "/drive", label: "Google Drive", icon: Cloud },
  ] },
  { to: "/processo", label: "Processi", icon: CheckSquare },
  { to: "/impostazioni", label: "Impostazioni", icon: Settings },
];

const slug = (s) => s.toLowerCase().replace(/\s+/g, "-");
const linkCls = ({ isActive }) => `nav-item flex h-11 items-center gap-3 rounded-md px-3 text-sm text-slate-400 hover:text-slate-100 hover:bg-surface-2 lg:h-8 ${isActive ? "active" : ""}`;

function Group({ item, onNavigate, open, setOpen }) {
  const { pathname } = useLocation();
  const inside = item.children.some((c) => (c.end ? pathname === c.to : pathname.startsWith(c.to)));
  useEffect(() => { if (inside) setOpen(item.label); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [inside]);
  const toggle = () => setOpen(open ? null : item.label);
  const Icon = item.icon;
  return (
    <div>
      <button onClick={toggle} data-testid={`nav-group-${slug(item.label)}`} aria-expanded={open}
        className={`flex h-11 w-full items-center gap-3 rounded-md px-3 text-sm hover:bg-surface-2 hover:text-slate-100 lg:h-8 ${inside ? "text-slate-100" : "text-slate-400"}`}>
        <Icon className="h-[18px] w-[18px]" />
        <span className="flex-1 text-left">{item.label}</span>
        <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      <div className={`grid transition-[grid-template-rows] duration-200 ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
        <div className="overflow-hidden">
          <div className="ml-4 space-y-0.5 border-l border-line pl-2 py-0.5">
            {item.children.map((c) => <Leaf key={c.to} item={c} onNavigate={onNavigate} />)}
          </div>
        </div>
      </div>
    </div>
  );
}

function Leaf({ item, onNavigate }) {
  const Icon = item.icon;
  return (
    <NavLink to={item.to} end={item.end} onClick={onNavigate} data-testid={`nav-${slug(item.label)}`} className={linkCls}>
      <Icon className="h-[18px] w-[18px]" />{item.label}
    </NavLink>
  );
}

const GROUP_MODULE = { Vendite: "sales", Marketing: "marketing" };

export default function SidebarNav({ items, onNavigate, user }) {
  const [modules, setModules] = useState(() => { try { return JSON.parse(localStorage.getItem("athanor-modules")) || null; } catch { return null; } });
  useEffect(() => {
    if (user?.role === "admin" && !user?.impersonated_by) return;
    api.get("/settings").then(({ data }) => { const m = data.modules || { sales: true, marketing: true, secretary: true }; setModules(m); localStorage.setItem("athanor-modules", JSON.stringify(m)); }).catch(() => {});
  }, [user?.user_id]);
  const mods = modules || { sales: true, marketing: true, secretary: true };
  const [openGroup, setOpenGroup] = useState(null);
  items = items.filter((it) => !GROUP_MODULE[it.label] || mods[GROUP_MODULE[it.label]] !== false)
    .map((it) => (it.children ? { ...it, children: it.children.filter((c) => !c.onlyIfNoMarketing || mods.marketing === false) } : it));
  return items
    .map((it) => (it.children ? { ...it, children: it.children.filter((c) => canSee(user, c.to)) } : it))
    .filter((it) => (it.children ? it.children.length > 0 : canSee(user, it.to)))
    .map((it) => it.children
      ? <Group key={it.label} item={it} onNavigate={onNavigate} open={openGroup === it.label} setOpen={setOpenGroup} />
      : <Leaf key={it.to} item={it} onNavigate={onNavigate} />);
}
